import { isOrbisErrorData } from '@/components/~advisor/Trading/Trading/utils';
import { selectRequireNbbo } from '@oneview/redux/settings/settingSelectors';
import { createQueryFn } from '@oneview/redux/utils';
import { BasketOrderValues } from '@orbis-systems/orbis-api-middleware';
import {
    getTradingFormMeta,
    ModifyOrderFormValues,
    showToast,
    TradingFormValues,
} from '@orbis-systems/orbis-react-toolkit';
import { api } from './baseApi';
import { DataTags } from './dataTags';
import { prepareOrder } from '../utils/api/prepareOrder';

export const tradingApi = api.injectEndpoints({
    endpoints: (build) => ({
        previewOrder: build.mutation({
            queryFn: createQueryFn(
                async (values: TradingFormValues, { extra: api, getState }) => {
                    const requireNbbo = selectRequireNbbo(getState());
                    const meta = getTradingFormMeta(values);
                    return api.orbisMiddleware.trading.previewOrder(values, meta, requireNbbo);
                },
                {
                    pendingMessage: 'Getting order preview…',
                    alertIfError: false,
                },
            ),
        }),
        previewAlgoOrder: build.mutation({
            queryFn: createQueryFn(
                async (values, { extra: api, getState }) => {
                    const requireNbbo = selectRequireNbbo(getState());

                    const response = await api.orbis.orders.v2.equities.algoPreview({
                        requireNbbo,
                        ...values,
                    });

                    return response.data;
                },
                {
                    pendingMessage: 'Getting order preview…',
                    alertIfError: false,
                },
            ),
        }),
        placeOrder: build.mutation({
            queryFn: createQueryFn(async (values: TradingFormValues, { extra: api, dispatch, getState }) => {
                const requireNbbo = selectRequireNbbo(getState());
                const meta = getTradingFormMeta(values);
                const {
                    modelTrading,
                    inventoryTrading,
                    mutualFundTrading,
                    optionTrading,
                    spreadTrading,
                    buyWrite,
                    bondTrading,
                } = meta;

                const orderBody = prepareOrder(values, meta, requireNbbo);

                let response;

                if (bondTrading) {
                    response = await api.orbis.orders.v2.fixedIncome.bondPlacement(orderBody);
                } else if (mutualFundTrading) {
                    response = await api.orbis.orders.v2.mutualFunds.mutualFundPlacement(orderBody);
                } else if (optionTrading) {
                    if (spreadTrading || buyWrite) {
                        response = await api.orbis.orders.v2.options.placementMultileg(orderBody);
                    } else {
                        response = await api.orbis.orders.v2.options.placement(orderBody);
                    }
                } else if (modelTrading || inventoryTrading) {
                    response = await api.orbis.orders.v2.equities.advisoryPlacement(orderBody);
                } else {
                    response = await api.orbis.orders.v2.equities.placement(orderBody);
                }

                const data = {
                    orderRef: response.data.orderRef || response.data.OrderRef,
                    market: response.data.market,
                };

                dispatch(showToast('Order has been placed'));
                return data;
            }),
            invalidatesTags: [DataTags.GetAccountOrders],
        }),
        placeAlgoOrder: build.mutation({
            queryFn: createQueryFn(async (values, { extra: api, dispatch, getState }) => {
                const requireNbbo = selectRequireNbbo(getState());

                const data = await api.orbis.orders.v2.equities.algoPlacement({
                    requireNbbo,
                    ...values,
                });
                dispatch(showToast('Order has been placed'));
                return data.data;
            }),
            invalidatesTags: [DataTags.GetAccountOrders],
        }),
        previewBasketOrder: build.mutation({
            queryFn: createQueryFn(
                (
                    {
                        accountId,
                        values,
                    }: {
                        accountId: number;
                        values: BasketOrderValues[];
                    },
                    { extra: api, getState },
                ) => {
                    const requireNbbo = selectRequireNbbo(getState());
                    return api.orbisMiddleware.trading.previewBasketOrder(accountId, values, requireNbbo);
                },
                {
                    pendingMessage: 'Getting basket orders preview…',
                },
            ),
        }),
        placeBasketOrder: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        accountId,
                        values,
                    }: {
                        accountId: number;
                        values: BasketOrderValues[];
                    },
                    { extra: api, dispatch, getState },
                ) => {
                    const requireNbbo = selectRequireNbbo(getState());
                    const data = await api.orbisMiddleware.trading.placeBasketOrder(accountId, values, requireNbbo);
                    dispatch(showToast('Order has been placed'));
                    return data;
                },
            ),
            invalidatesTags: [
                DataTags.GetAccountDriftReport,
                DataTags.GetAccountOrders,
                DataTags.GetBranchAccount,
                DataTags.GetAccountPortfolio,
                DataTags.GetAccountAvailableCashForWithdrawal,
                DataTags.GetAccountBuyingPower,
            ],
        }),
        cancelOrder: build.mutation({
            queryFn: createQueryFn(
                async (
                    { orderRef, accountId, marketCode }: { orderRef: string; accountId: number; marketCode: string },
                    { extra: api, dispatch },
                ) => {
                    const response = await api.orbisMiddleware.trading.cancelOrder(orderRef, accountId, marketCode);
                    dispatch(showToast(`Order ${orderRef} has been canceled`));
                    return response;
                },
            ),
        }),
        modifyOrder: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        values,
                        accountId,
                        accountNumber,
                        marketCode,
                    }: { values: ModifyOrderFormValues; accountId: number; accountNumber: string; marketCode: string },
                    { extra: api, dispatch },
                ) => {
                    try {
                        const orderRef = await api.orbisMiddleware.trading.modifyOrder({
                            values,
                            accountId,
                            accountNumber,
                            marketCode,
                        });

                        dispatch(showToast(`Order ${orderRef} has been modified.`));
                        return orderRef;
                    } catch (error) {
                        const resError = isOrbisErrorData(error)
                            ? error?.orbisErrorMessage || error.orbisErrorCode
                            : null;
                        const errorDetails = resError ? `(${resError})` : '';

                        dispatch(showToast(`Failed to modify order ${values.orderRef} ${errorDetails}`));
                        throw error;
                    }
                },
            ),
            invalidatesTags: (_result, _error, { values }) => [{ type: DataTags.GetOrderDetails, id: values.orderRef }],
        }),
        getOrderDetails: build.query({
            queryFn: createQueryFn(
                (
                    { accountId, orderRef, market }: { accountId: number; orderRef: string; market: string },
                    { extra: api },
                ) => api.orbisMiddleware.trading.getOrderDetails(accountId, orderRef, market),
            ),
            providesTags: (_result, _error, { orderRef }) => [{ type: DataTags.GetOrderDetails, id: orderRef }],
        }),
        getTradeableCurrencies: build.query({
            queryFn: createQueryFn((_: void, { extra: api }) => api.orbisMiddleware.trading.getTradableCurrencyList()),
        }),
    }),
});

export const {
    usePreviewOrderMutation,
    usePlaceOrderMutation,
    usePreviewBasketOrderMutation,
    usePlaceBasketOrderMutation,
    useGetTradeableCurrenciesQuery,
    useCancelOrderMutation,
    useModifyOrderMutation,
    useGetOrderDetailsQuery,
    usePreviewAlgoOrderMutation,
    usePlaceAlgoOrderMutation,
} = tradingApi;
